import React from "react";
import PropTypes from "prop-types";
import { Link as MuiLink } from "@mui/material";

// Top stat widgets hidden — icons import commented out to avoid unused import lint errors
// import { Calendar, ClipboardPaste, Contact, Wallet } from "lucide-react";

// import CompanyOverviewStatCard from "../../../../../../components/Company/CompanyOverviewStatCard";
import CompanyEditDocuments from "../../../../../../components/Company/CompanyEditDocuments";
import Card from "../../../../../../components/Card";
import TruncatedWithTooltip from "../../../../../../components/TruncatedWithTooltip";
import { clx } from "../../../../../../utils/Helpers";

import "./_OverviewTab.scss";

// Top stat widgets hidden — revenue/number formatters commented out to avoid unused lint errors
// const formatOverviewRevenue = (raw, currencyLabel) => {
//   if (raw === null || raw === undefined || raw === "") return null;
//   const n = Number(raw);
//   if (Number.isNaN(n)) return String(raw);
//   if (n >= 1_000_000) {
//     const v = n / 1_000_000;
//     const rounded =
//       v % 1 === 0
//         ? String(Math.round(v))
//         : String(v.toFixed(1)).replace(/\.0$/, "");
//     return `${currencyLabel} ${rounded}M`;
//   }
//   if (n >= 1000) {
//     return `${currencyLabel} ${Math.round(n / 1000)}K`;
//   }
//   return `${currencyLabel} ${n}`;
// };

// const overviewStatNumber = (raw) => {
//   if (raw === null || raw === undefined || raw === "") return null;
//   const n = Number(raw);
//   return Number.isNaN(n) ? raw : n;
// };

const EMPTY_VALUE = "-";
// const STAT_ICON_SIZE = 24;

const OverviewField = ({ label, value, children, valueClassName }) => (
  <div className="company-overview-field">
    <div className="company-overview-field__label">{label}</div>
    <div className={clx("company-overview-field__value", valueClassName)}>
      {children ?? value ?? EMPTY_VALUE}
    </div>
  </div>
);

OverviewField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  children: PropTypes.node,
  valueClassName: PropTypes.string,
};

OverviewField.defaultProps = {
  value: EMPTY_VALUE,
  children: null,
  valueClassName: "",
};

const OverviewTab = ({
  company,
  lang,
  registrationMessages,
  companiesListMessages,
}) => {
  // Top stat widgets hidden — stat cards config commented out to avoid unused lint errors
  // const statCards = useMemo(() => {
  //   const totalEvents =
  //     company?.events_count ??
  //     company?.number_of_events ??
  //     company?.total_events;
  //   const ongoing =
  //     company?.ongoing_events_count ??
  //     company?.ongoing_events ??
  //     company?.active_events_count;
  //   const revenueRaw =
  //     company?.total_revenue ??
  //     company?.revenue_total ??
  //     company?.events_revenue;
  //   const applicants =
  //     company?.total_applicants ??
  //     company?.applicants_count ??
  //     company?.applicants_total;
  //
  //   return [
  //     {
  //       key: "events",
  //       label: companiesListMessages.overviewStatTotalEvents,
  //       value: overviewStatNumber(totalEvents),
  //       variant: "events",
  //       icon: <Calendar size={STAT_ICON_SIZE} aria-hidden />,
  //     },
  //     {
  //       key: "ongoing",
  //       label: companiesListMessages.overviewStatOngoingEvents,
  //       value: overviewStatNumber(ongoing),
  //       variant: "ongoing",
  //       icon: <ClipboardPaste size={STAT_ICON_SIZE} aria-hidden />,
  //     },
  //     {
  //       key: "revenue",
  //       label: companiesListMessages.overviewStatTotalRevenue,
  //       value: formatOverviewRevenue(
  //         revenueRaw,
  //         companiesListMessages.overviewStatCurrency,
  //       ),
  //       variant: "revenue",
  //       icon: <Wallet size={STAT_ICON_SIZE} aria-hidden />,
  //     },
  //     {
  //       key: "applicants",
  //       label: companiesListMessages.overviewStatTotalApplicants,
  //       value: overviewStatNumber(applicants),
  //       variant: "applicants",
  //       icon: <Contact size={STAT_ICON_SIZE} aria-hidden />,
  //     },
  //   ];
  // }, [company, companiesListMessages]);

  const owner = company.company_owner || {};
  const city =
    lang === "ar"
      ? company.city?.name_ar || company.city?.name_en
      : company.city?.name_en || company.city?.name_ar;
  const phoneNumber = owner.phone_number
    ? `${owner?.country?.dial_code || ""}${owner.phone_number}`
    : EMPTY_VALUE;

  return (
    <div className="company-overview-tab">
      {/* Top stat widgets hidden */}
      {/* <div className="company-overview-stats-grid">
        {statCards.map((card) => (
          <CompanyOverviewStatCard
            key={card.key}
            label={card.label}
            value={card.value}
            icon={card.icon}
            variant={card.variant}
          />
        ))}
      </div> */}

      <div className="company-view-layout">
        <div className="company-view-main">
          <Card
            title={registrationMessages.companyInformation}
            classNames={{ body: "company-overview-card__body" }}
          >
            <div className="company-overview-field-grid company-overview-field-grid--cols-2">
              <OverviewField label={companiesListMessages.englishName}>
                <TruncatedWithTooltip
                  title={company.name_en || EMPTY_VALUE}
                  lines={2}
                >
                  <span>{company.name_en || EMPTY_VALUE}</span>
                </TruncatedWithTooltip>
              </OverviewField>
              <OverviewField label={companiesListMessages.arabicName}>
                <TruncatedWithTooltip
                  title={company.name_ar || EMPTY_VALUE}
                  lines={2}
                >
                  <span>{company.name_ar || EMPTY_VALUE}</span>
                </TruncatedWithTooltip>
              </OverviewField>
              <OverviewField
                label={companiesListMessages.city}
                value={city || EMPTY_VALUE}
              />
              <OverviewField label={companiesListMessages.commercialRecordLabel}>
                {company.commercial_record_url ? (
                  <MuiLink
                    href={company.commercial_record_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {company.commercial_record || EMPTY_VALUE}
                  </MuiLink>
                ) : (
                  company.commercial_record || EMPTY_VALUE
                )}
              </OverviewField>
            </div>
          </Card>
          <Card
            title={companiesListMessages.contactPerson}
            classNames={{ body: "company-overview-card__body" }}
          >
            <div className="company-overview-field-grid company-overview-field-grid--cols-3">
              <OverviewField
                label={companiesListMessages.fullName}
                value={owner.name || EMPTY_VALUE}
              />
              <OverviewField
                label={companiesListMessages.mobileNumber}
                value={phoneNumber}
                valueClassName="company-overview-field__value--phone"
              />
              <OverviewField label={companiesListMessages.emailAddress}>
                {owner.email ? (
                  <MuiLink href={`mailto:${owner.email}`} underline="hover">
                    {owner.email}
                  </MuiLink>
                ) : (
                  EMPTY_VALUE
                )}
              </OverviewField>
            </div>
          </Card>
        </div>
        <aside className="company-view-aside">
          <CompanyEditDocuments
            commercialRecordUrl={company.commercial_record_url}
            delegationDocumentUrl={owner.delegation_document_url}
            idDocumentUrl={owner.personal_id_url}
            pendingDocs={{}}
            onDocumentChange={() => {}}
            showReplace={false}
            showMissingDocuments={false}
          />
        </aside>
      </div>
    </div>
  );
};

OverviewTab.propTypes = {
  company: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  registrationMessages: PropTypes.object.isRequired,
  companiesListMessages: PropTypes.object.isRequired,
};

export default OverviewTab;
